import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import MesfModal from "../../../../../../components/modals/modal.mesf";
import { ButtonWithLoading } from "../../../../../../components/shared/buttons/button-with-loading";
import type { ViewTag, ViewTagsMap } from "../../models/TrendingModelsV2";

interface Props {
	open: boolean;
	handleClose: () => void;
	viewTags: ViewTagsMap;
	onAddBits: (sourceTagId: number, bitIndices: number[]) => void;
	existingBitTags: Set<string>; // Set of "sourceTagId-bitIndex" strings
}

export const BitSelectorModal = ({
	open,
	handleClose,
	viewTags,
	onAddBits,
	existingBitTags,
}: Props) => {
	const [selectedTag, setSelectedTag] = useState<ViewTag | null>(null);
	const [selectedBits, setSelectedBits] = useState<Set<number>>(new Set());
	const [isLoading, setIsLoading] = useState(false);

	// Get list of non-bit-extracted tags (source tags only)
	const availableTags = useMemo(() => {
		return Object.values(viewTags)
			.filter((t) => t && t.viewTag && !t.viewTag.IsBitExtracted)
			.map((t) => t.viewTag)
			.sort((a, b) => a.TagName.localeCompare(b.TagName));
	}, [viewTags]);

	// Check if a specific bit is already extracted for the selected tag
	const isBitAlreadyExtracted = useCallback(
		(bitIndex: number): boolean => {
			if (!selectedTag) return false;
			return existingBitTags.has(`${selectedTag.TagId}-${bitIndex}`);
		},
		[selectedTag, existingBitTags],
	);

	// Handle bit checkbox change
	const handleBitToggle = useCallback((bitIndex: number) => {
		setSelectedBits((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(bitIndex)) {
				newSet.delete(bitIndex);
			} else {
				newSet.add(bitIndex);
			}
			return newSet;
		});
	}, []);

	// Select all available bits
	const handleSelectAll = useCallback(() => {
		const availableBits = new Set<number>();
		for (let i = 0; i < 32; i++) {
			if (!isBitAlreadyExtracted(i)) {
				availableBits.add(i);
			}
		}
		setSelectedBits(availableBits);
	}, [isBitAlreadyExtracted]);

	// Clear all selections
	const handleClearAll = useCallback(() => {
		setSelectedBits(new Set());
	}, []);

	// Handle tag selection change
	const handleTagChange = useCallback((_event: any, value: ViewTag | null) => {
		setSelectedTag(value);
		setSelectedBits(new Set()); // Clear bit selection when tag changes
	}, []);

	// Handle add button click
	const handleAdd = useCallback(() => {
		if (!selectedTag || selectedBits.size === 0) return;

		setIsLoading(true);
		onAddBits(
			selectedTag.TagId,
			Array.from(selectedBits).sort((a, b) => a - b),
		);
		setIsLoading(false);

		// Reset state and close
		setSelectedTag(null);
		setSelectedBits(new Set());
		handleClose();
	}, [selectedTag, selectedBits, onAddBits, handleClose]);

	// Handle cancel
	const handleCancel = useCallback(() => {
		setSelectedTag(null);
		setSelectedBits(new Set());
		handleClose();
	}, [handleClose]);

	// Render bit grid (4 rows x 8 columns)
	const renderBitGrid = () => {
		if (!selectedTag) {
			return (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						minHeight: "200px",
						color: "text.secondary",
					}}
				>
					<Typography>Select a tag to view available bits</Typography>
				</Box>
			);
		}

		const rows = [];
		for (let row = 0; row < 4; row++) {
			const bits = [];
			for (let col = 0; col < 8; col++) {
				const bitIndex = row * 8 + col;
				const isExtracted = isBitAlreadyExtracted(bitIndex);
				const isSelected = selectedBits.has(bitIndex);

				bits.push(
					<Grid2 key={bitIndex} size={1.5}>
						<FormControlLabel
							control={
								<Checkbox
									checked={isSelected}
									onChange={() => handleBitToggle(bitIndex)}
									disabled={isExtracted}
									size="small"
									sx={{
										padding: "4px",
									}}
								/>
							}
							label={
								<Typography
									variant="body2"
									sx={{
										color: isExtracted ? "text.disabled" : "text.primary",
										fontSize: "0.8rem",
										minWidth: "20px",
									}}
								>
									{bitIndex}
								</Typography>
							}
							sx={{
								margin: 0,
								"& .MuiFormControlLabel-label": {
									marginLeft: "2px",
								},
							}}
						/>
					</Grid2>,
				);
			}
			rows.push(
				<Grid2 container key={row} spacing={0.5} sx={{ mb: 0.5 }}>
					{bits}
				</Grid2>,
			);
		}
		return rows;
	};

	const selectedCount = selectedBits.size;
	const availableCount = selectedTag
		? 32 -
			Array.from({ length: 32 }, (_, i) => i).filter(isBitAlreadyExtracted)
				.length
		: 0;

	return (
		<MesfModal
			open={open}
			handleClose={handleCancel}
			maxWidth="md"
			aria-labelledby="bit-selector-modal-title"
			aria-describedby="bit-selector-modal-description"
			title="Add Bits from Bit Array"
		>
			<MesfModal.Content dividers>
				<Grid2 container spacing={2}>
					{/* Tag Selection */}
					<Grid2 size={12}>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Select Source Tag
						</Typography>
						<Autocomplete
							size="small"
							id="source-tag-selector"
							options={availableTags}
							getOptionLabel={(option) => option.Alias || option.TagName}
							value={selectedTag}
							onChange={handleTagChange}
							noOptionsText="No tags available in current view"
							renderOption={(props, option) => (
								<Box {...props} component="li">
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											width: "100%",
										}}
									>
										<Typography variant="body2">
											{option.Alias || option.TagName}
										</Typography>
										<Typography
											variant="caption"
											sx={{ color: "text.secondary", ml: 2 }}
										>
											{option.TagName !== option.Alias ? option.TagName : ""}
										</Typography>
									</Box>
								</Box>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Source Tag"
									variant="outlined"
									placeholder="Search tags..."
								/>
							)}
							sx={{ width: "100%" }}
						/>
					</Grid2>

					{/* Bit Selection Header */}
					<Grid2 size={12}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mt: 1,
							}}
						>
							<Typography variant="subtitle2">
								Select Bit Positions (0-31, LSB = 0)
							</Typography>
							{selectedTag && (
								<Box sx={{ display: "flex", gap: 1 }}>
									<Button
										size="small"
										variant="outlined"
										onClick={handleSelectAll}
										disabled={availableCount === 0}
									>
										Select All
									</Button>
									<Button
										size="small"
										variant="outlined"
										onClick={handleClearAll}
										disabled={selectedCount === 0}
									>
										Clear
									</Button>
								</Box>
							)}
						</Box>
					</Grid2>

					{/* Bit Grid */}
					<Grid2 size={12}>
						<Box
							sx={{
								border: "1px solid",
								borderColor: "divider",
								borderRadius: 1,
								p: 2,
								minHeight: "200px",
								backgroundColor: "background.paper",
							}}
						>
							{renderBitGrid()}
						</Box>
					</Grid2>

					{/* Selection Summary */}
					{selectedTag && (
						<Grid2 size={12}>
							<Typography variant="body2" sx={{ color: "text.secondary" }}>
								{selectedCount} bit{selectedCount !== 1 ? "s" : ""} selected
								{availableCount < 32 &&
									` (${32 - availableCount} already extracted)`}
							</Typography>
						</Grid2>
					)}
				</Grid2>
			</MesfModal.Content>
			<MesfModal.Actions>
				<Box sx={{ display: "flex", gap: 1, pt: 1 }}>
					<Button variant="outlined" color="secondary" onClick={handleCancel}>
						Cancel
					</Button>
					<ButtonWithLoading
						onClick={handleAdd}
						variant="contained"
						color="primary"
						isLoading={isLoading}
						disabled={!selectedTag || selectedCount === 0}
					>
						Add {selectedCount > 0 ? `(${selectedCount})` : ""}
					</ButtonWithLoading>
				</Box>
			</MesfModal.Actions>
		</MesfModal>
	);
};
